import { memo, useCallback } from "react";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";
import DriverDropdown from "ui/components/Dropdown/DriverDropdown/DriverDropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

import { VehicleTransferDetailsFormProps } from "./types";

/**
 * Drugi korak premještaja — podaci koji vrijede za svaku vrstu lokacije: od kada
 * premještaj vrijedi i zašto. Odvojeni su od odabira lokacije da prvi korak ostane
 * jedno pitanje: gdje je vozilo.
 */
const VehicleTransferDetailsForm: React.FC<VehicleTransferDetailsFormProps> = (props) => {
  const { values, errors, onChange, vehicleId } = props;

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleDriverChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({ ...values, driverId: item.value || null });
    },
    [values, onChange]
  );

  return (
    <div className="flex items-start justify-center px-4 pt-12 pb-6">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1mb-2">
          Ostale informacije
        </h1>
        <p className="text-center text-md text-slate-500 mb-8">
          Zabilježite kada je premještaj obavljen i tko je vozilo odvezao.
        </p>

        {/* Sekcije su odvojene crtom preko cijele kartice, s naslovom lijevo i objašnjenjem
            desno — isti raspored kao korak "Informacije" pri unosu vozila. */}
        <div className="border bg-white border-slate-200">
          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Naziv i datum
              </h3>
              <p className="text-xs text-slate-500">
                Datum je predložen na današnji dan — promijenite ga za naknadni unos
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {/* Naziv je slobodan tekst i uzima dvije trećine retka, datum jednu. */}
              <div className="col-span-2">
                <TextField
                  type="text"
                  name="name"
                  label="Naziv"
                  placeholder="npr. Isporuka kupcu"
                  value={values.name}
                  onChange={handleTextChange}
                  error={errors.name}
                  required
                  width="full"
                />
              </div>
              <div>
                <DatePicker
                  name="validFrom"
                  label="Datum premještaja"
                  placeholder="Datum premještaja"
                  value={values.validFrom}
                  onChange={handleDateChange}
                  error={errors.validFrom}
                  required
                  width="full"
                />
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Vozač
              </h3>
              <p className="text-xs text-slate-500">
                Tko je vozilo odvezao na novu lokaciju
              </p>
            </div>
            <DriverDropdown
              name="driverId"
              placeholder="Odaberite vozača"
              vehicleId={vehicleId}
              value={values.driverId}
              onChange={handleDriverChange}
              error={errors.driverId}
              required
              width="full"
            />
          </div>

          {/* Napomena je zadnja i najšira — piše se u par rečenica, ne u jedan redak. */}
          <div className="py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Napomena
              </h3>
              <p className="text-xs text-slate-500">
                Zašto je vozilo premješteno, ako treba zabilježiti
              </p>
            </div>
            <TextArea
              name="note"
              placeholder="Napomena"
              value={values.note}
              onChange={handleTextChange}
              error={errors.note}
              rows={4}
              size="full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleTransferDetailsForm);
